/* eslint-disable react-refresh/only-export-components -- small helpers live beside their components */
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/format'
import { CheckIcon, CopyIcon } from './icons'
import { buttonClasses, type ButtonSize, type ButtonVariant } from './buttonClasses'

export interface CopyButtonProps {
  /** The text that goes on the clipboard. */
  value: string
  /** Visible label (default "Copy"). */
  label?: string
  /** Accessible name when the label alone is ambiguous, e.g. "Copy CNAME target". */
  ariaLabel?: string
  size?: ButtonSize
  variant?: ButtonVariant
  className?: string
}

/** Copy text; true when it reached the clipboard (falls back to execCommand). */
export async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* fall through to the textarea fallback */
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand?.('copy') ?? false
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

/** Copies `value` and says so for 1.5s ("Copied" / "Press ⌘C" when blocked). */
export function CopyButton({ value, label = 'Copy', ariaLabel, size = 'sm', variant = 'secondary', className }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    const ok = await writeClipboard(value)
    setState(ok ? 'copied' : 'failed')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setState('idle'), 1500)
  }

  return (
    <button type="button" onClick={copy} aria-label={ariaLabel} className={buttonClasses({ size, variant, className: cn('shrink-0', className) })}>
      {state === 'copied' ? <CheckIcon size={14} className="text-good" /> : <CopyIcon size={14} />}
      <span aria-live="polite">{state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy blocked' : label}</span>
    </button>
  )
}
